/**
 * Window totals a dashboard tile reads.
 *
 * Every field is nullable for the same reason: `spend` is null because a project made
 * only of ad-serving campaigns reports no spend at all, and `impressions`/`clicks`/
 * `conversions` are null when the totals themselves are unknown (a settled query with no
 * data, e.g. a cache eviction) rather than a measured zero.
 */
export interface DeliveryTotals {
  spend: number | null;
  impressions: number | null;
  clicks: number | null;
  conversions: number | null;
}

export interface DerivedRates {
  ctr: number | null;
  cpc: number | null;
  cpm: number | null;
  cpa: number | null;
  cvr: number | null;
}

/** Divide, or say nothing. Never 0 — see `absent is never zero` in the spec. */
function ratio(numerator: number | null, denominator: number | null): number | null {
  if (numerator === null || denominator === null || denominator === 0) return null;
  return numerator / denominator;
}

/**
 * Derives rates from SUMMED totals, never by averaging daily rates — averaging a rate
 * weights a quiet day the same as a heavy one and produces a number matching no platform
 * report.
 */
export function deriveRates(totals: DeliveryTotals): DerivedRates {
  return {
    ctr: ratio(totals.clicks, totals.impressions),
    cpc: ratio(totals.spend, totals.clicks),
    cpm:
      totals.spend === null || totals.impressions === null || totals.impressions === 0
        ? null
        : (totals.spend / totals.impressions) * 1000,
    cpa: ratio(totals.spend, totals.conversions),
    cvr: ratio(totals.conversions, totals.clicks),
  };
}

/**
 * Signed fractional change against the previous window.
 *
 * Null when the previous window was zero: growth from nothing is undefined, and every
 * alternative ("+100%", "+∞") states a fact we do not have.
 */
export function percentChange(current: number | null, previous: number | null): number | null {
  if (current === null || previous === null || previous === 0) return null;
  return (current - previous) / previous;
}

const MS_PER_DAY = 86_400_000;

function toIsoDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/**
 * The equal-length window immediately before this one, ending the day before it starts.
 *
 * UTC-safe parsing is required: every date construction must include the `T00:00:00Z` suffix,
 * every accessor must use `getTime()` or `toISOString()`, never `getDate()`, `getMonth()`,
 * or `setDate()`. A local-time accessor shifts the result by a day for users west of UTC,
 * causing silent comparison against the wrong week.
 *
 * **CAVEAT: The test suite cannot catch this regression.** Vitest pins `TZ: 'UTC'` at startup;
 * a runtime `process.env.TZ` change does not re-initialize the Date object, so tests always
 * run in UTC. A refactor replacing `T00:00:00Z` with `T00:00:00` or using `getDate()` would
 * pass the test suite but fail for real users in non-UTC timezones. This comment is the only
 * guard — keep it and the UTC calls explicit.
 */
export function previousWindow(
  startDate: string,
  endDate: string,
): { startDate: string; endDate: string } {
  const start = new Date(`${startDate}T00:00:00Z`);
  const end = new Date(`${endDate}T00:00:00Z`);
  const inclusiveDays = Math.round((end.getTime() - start.getTime()) / MS_PER_DAY) + 1;

  const prevEnd = new Date(start.getTime() - MS_PER_DAY);
  const prevStart = new Date(prevEnd.getTime() - (inclusiveDays - 1) * MS_PER_DAY);

  return { startDate: toIsoDate(prevStart), endDate: toIsoDate(prevEnd) };
}
